import React from 'react';
import Modal from 'react-modal';
import { Button } from '../../../components/Button';
import { ReactComponent as CloseIcon } from '../../../assets/icons/x.svg';
import { ReactComponent as CheckIcon } from '../../../assets/icons/check.svg';
import { Image } from '../../../api/common';
import { PromotionPayload } from '../../../api/generated';
import { hasOverlappingPromotions } from '../utils/promotionOverlap';

interface ChecklistItem {
  label: string;
  isComplete: boolean;
  isOptional?: boolean;
  isWarning?: boolean;
}

interface DraftFormData {
  description: string;
  businessLink: string;
  businessCategoryIds: string[];
  paymentProviderIds: string[];
  logo: Image | null;
  mainImage: Image | null;
  additionalImages: Image[];
  promotions: PromotionPayload[];
}

interface SubmitApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => Promise<void>;
  formData?: DraftFormData;
  isSubmitting?: boolean;
}

const WarningIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2L1 21h22L12 2zm0 3.83L19.53 19H4.47L12 5.83zM11 10v4h2v-4h-2zm0 6v2h2v-2h-2z" />
  </svg>
);

const ChecklistItemRow: React.FC<{ item: ChecklistItem }> = ({ item }) => {
  const getIcon = () => {
    if (item.isWarning) {
      return (
        <span className="flex items-center justify-center w-5 h-5">
          <WarningIcon className="w-5 h-5 text-orange-500" />
        </span>
      );
    }
    if (item.isComplete) {
      return (
        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-green-100">
          <CheckIcon className="w-3 h-3 text-green-600" />
        </span>
      );
    }
    if (item.isOptional) {
      return (
        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-gray-100">
          <CloseIcon className="w-2.5 h-2.5 text-gray-400" />
        </span>
      );
    }
    return (
      <span className="flex items-center justify-center w-5 h-5 rounded-full bg-red-100">
        <CloseIcon className="w-2.5 h-2.5 text-red-500" />
      </span>
    );
  };

  return (
    <li className="flex items-center gap-3">
      {getIcon()}
      <span className={item.isOptional && !item.isComplete ? 'text-gray-400' : 'text-gray-700'}>
        {item.label}
        {item.isOptional && <span className="text-gray-400 text-sm ml-1">(optional)</span>}
      </span>
    </li>
  );
};

export const SubmitApplicationModal: React.FC<SubmitApplicationModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  formData,
  isSubmitting = false,
}) => {
  const getChecklistItems = (): ChecklistItem[] => {
    if (!formData) return [];

    const items: ChecklistItem[] = [
      {
        label: 'Business description',
        isComplete: Boolean(formData.description && formData.description.trim().length > 0),
      },
      {
        label: 'Business website',
        isComplete: Boolean(formData.businessLink && formData.businessLink.trim().length > 0),
      },
      {
        label: 'At least one category',
        isComplete: formData.businessCategoryIds.length > 0,
      },
      {
        label: 'At least one payment provider',
        isComplete: formData.paymentProviderIds.length > 0,
      },
      {
        label: 'Logo image',
        isComplete: Boolean(formData.logo),
      },
      {
        label: 'Main image',
        isComplete: Boolean(formData.mainImage),
      },
      {
        label: 'Additional images',
        isComplete: formData.additionalImages.length > 0,
        isOptional: true,
      },
      {
        label: 'At least one promotion',
        isComplete: formData.promotions.length > 0,
      },
    ];

    if (hasOverlappingPromotions(formData.promotions)) {
      items.push({
        label: 'Some promotions overlap based on constraints',
        isComplete: false,
        isWarning: true,
      });
    }

    return items;
  };

  const checklistItems = getChecklistItems();
  const countableItems = checklistItems.filter((item) => !item.isWarning);
  const completedCount = countableItems.filter((item) => item.isComplete).length;
  const totalCount = countableItems.length;
  const requiredItems = countableItems.filter((item) => !item.isOptional);
  const allRequiredComplete = requiredItems.every((item) => item.isComplete);

  // Sort: completed first, then missing optional, then missing required, then warnings
  const sortedChecklistItems = [...checklistItems].sort((a, b) => {
    const getOrder = (item: ChecklistItem) => {
      if (item.isWarning) return 4;
      if (item.isComplete) return 1;
      if (item.isOptional) return 2;
      return 3; // missing required
    };
    return getOrder(a) - getOrder(b);
  });

  return (
    <Modal
      className="modal-content md:max-w-[500px]"
      overlayClassName="modal-overlay"
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Submit for Review"
    >
      <div className="flex justify-between mb-6">
        <h2 className="text-primary font-medium text-xl">
          Submit for Review
        </h2>
        <Button onClick={onClose} variant="icon">
          <CloseIcon className="w-3 h-3" />
        </Button>
      </div>

      <div className="space-y-4">
        {formData && (
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-gray-900">Checklist</h3>
              <span
                className={`text-sm font-medium px-2 py-0.5 rounded ${
                  allRequiredComplete
                    ? 'bg-green-100 text-green-700'
                    : 'bg-orange-100 text-orange-700'
                }`}
              >
                {completedCount}/{totalCount}
              </span>
            </div>
            <ul className="space-y-2.5">
              {sortedChecklistItems.map((item, index) => (
                <ChecklistItemRow key={index} item={item} />
              ))}
            </ul>
          </div>
        )}

        <p className="text-sm text-gray-600">
          Your application will be submitted for review. Our staff will review
          it, contact you if needed, and approve it. Your business will then
          be published to{' '}
          <span className="font-medium">app.oisterbonus.dk</span>.
        </p>
      </div>

      <div className="flex gap-3 justify-end mt-6">
        <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          type="button"
          disabled={!allRequiredComplete || isSubmitting}
          isLoading={isSubmitting}
          onClick={onSubmit}
        >
          Submit for Review
        </Button>
      </div>
    </Modal>
  );
};
